import axios, { type AxiosInstance } from "axios";
import { storeToRefs } from "pinia";
import { ToastSeverity } from "primevue/api";
import { useToastService } from "@/composables/useToastService";
import { useAuthenticationStore } from "@/stores/authentication";
import { EnvironmentService } from "@/utils/environment";

type ServiceType = "consumer" | "edge";

const apiClients: { [key: string]: AxiosInstance } = {};

const getBaseUrl = (
    serviceType: ServiceType,
    communityOverride?: string,
): string => {
    const { env, community: hostCommunity } =
        EnvironmentService.parseEnvironment();

    // Local backend override based on hostname
    if (env === "localhost") {
        if (serviceType === "consumer") {
            return (
                import.meta.env.VITE_CONSUMER_API_URL ||
                "http://localhost:3000/api/v2"
            );
        }
        if (serviceType === "edge") {
            return (
                import.meta.env.VITE_EDGE_API_URL ||
                "http://localhost:3001/api/v2"
            );
        }
    }

    const envPrefix = EnvironmentService.getEnvironmentPrefix();

    if (serviceType === "consumer") {
        return `https://consumer.${envPrefix}corridorsafety.com/api/v2`;
    }

    if (serviceType === "edge") {
        const targetCommunity = communityOverride ?? hostCommunity;
        // TODO: Remove this once we have a way to get the community from the URL
        // return `https://edge.${envPrefix}corridorsafety.com/api/v2`;
        if (!targetCommunity) {
            throw new Error("Edge API client requires a community context.");
        }
        return `https://edge.${envPrefix}${targetCommunity}.corridorsafety.com/api/v2`;
    }

    throw new Error(`Invalid service type: ${serviceType}`);
};

export const createApiClient = (
    serviceType: ServiceType,
    communityOverride?: string,
): AxiosInstance => {
    const communityKey = communityOverride || "default";
    const clientKey = `${serviceType}:${communityKey}`;

    if (apiClients[clientKey]) {
        return apiClients[clientKey];
    }

    const authenticationStore = useAuthenticationStore();
    const { showToast } = useToastService();

    const instance = axios.create({
        baseURL: getBaseUrl(serviceType, communityOverride),
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        // Serialize arrays as repeated keys: status=online&status=offline
        paramsSerializer: { indexes: null },
    });

    instance.interceptors.request.use(
        async (config) => {
            const { accessToken } = storeToRefs(authenticationStore);

            // Preserve existing headers
            config.headers = config.headers || {};

            // Add authorization header if we have a token
            if (accessToken.value) {
                config.headers["Authorization"] = `Bearer ${accessToken.value}`;
            }

            // Debug logging
            // console.log("Request config headers:", config.headers);

            return config;
        },
        (error) => {
            console.error("Request interceptor error", error);
            return Promise.reject(error);
        },
    );

    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                console.error("401, signing out");
                // window.location.href = "/signout";
            } else if (error.response?.status === 503) {
                // window.location.href = "/maintenance";
            } else if (error.response?.status === 500) {
                // window.location.href = "/servererror";
            } else if (error.code === "ERR_NETWORK") {
                showToast({
                    group: "secondarynotification",
                    severity: ToastSeverity.ERROR,
                    life: 5000,
                    summary: "Network error connecting to Corridor.",
                    icon: "far fa-wifi-slash",
                });
            } else if (error.code === "ERR_CANCELED") {
                // Silently ignore canceled requests (happens during query invalidation)
                // This is normal behavior when queries are invalidated while in-flight
                return Promise.reject(error);
            } else {
                console.error("Unhandled API error", error);
            }
            return Promise.reject(error);
        },
    );

    apiClients[clientKey] = instance;

    return instance;
};

export const getMqttUrl = (): string => {
    const { env, community: hostCommunity } =
        EnvironmentService.parseEnvironment();

    // Local backend override based on hostname
    if (env === "localhost") {
        return import.meta.env.VITE_MQTT_URL || "ws://localhost:3000/mqtt";
    }

    const envPrefix = EnvironmentService.getEnvironmentPrefix();

    // Use edge service pattern for MQTT WebSocket
    if (!hostCommunity) {
        throw new Error("MQTT client requires a community context.");
    }
    return `wss://edge.${envPrefix}${hostCommunity}.corridorsafety.com/mqtt`;
};

export { getBaseUrl };
